import { useState } from 'react';
import { useStoreCoverage, useStoreGaps, useStoreVerdict } from '../../api/coverage';
import type { StoreCoverage as Coverage, StoreGap } from '../../api/coverage';

function percent(part: number, whole: number): string {
  if (!whole) return '—';
  return `${((100 * part) / whole).toFixed(1)}%`;
}

function StoreCard({
  store,
  selected,
  onSelect,
}: {
  store: Coverage;
  selected: boolean;
  onSelect: () => void;
}) {
  const cookable = store.recipes_cookable;
  const total = store.recipes_total;

  return (
    <button
      type="button"
      className={`coverage-card${selected ? ' is-selected' : ''}`}
      aria-pressed={selected}
      onClick={onSelect}
    >
      <span className="coverage-card__name">{store.name}</span>

      {store.never_refreshed ? (
        /* Zero because nobody has ever looked, which reads very differently
           from zero because the shop stocks none of it. */
        <span className="coverage-card__empty">never refreshed</span>
      ) : (
        <>
          <span className="coverage-card__big">
            {total != null && cookable != null ? percent(cookable, total) : '—'}
          </span>
          <span className="coverage-card__label">
            recipes you can cook
            {total != null && cookable != null && (
              <em>
                {' '}
                {cookable.toLocaleString()} of {total.toLocaleString()}
              </em>
            )}
          </span>
          <span className="coverage-card__small">
            {percent(store.ingredients_matched, store.ingredients_total)} of
            ingredients matched
            <em>
              {' '}
              {store.ingredients_matched.toLocaleString()} of{' '}
              {store.ingredients_total.toLocaleString()}
            </em>
          </span>
          {Boolean(store.makeable) && (
            <span className="coverage-card__small">
              counting {store.makeable!.toLocaleString()} blends made from what's
              on the shelf
            </span>
          )}
        </>
      )}
    </button>
  );
}

function GapRow({ gap, storeId }: { gap: StoreGap; storeId: number }) {
  const verdict = useStoreVerdict(storeId);

  return (
    <li className="gap-row">
      <div className="gap-row__head">
        <strong>{gap.name}</strong>
        <span className="muted small">
          blocks {gap.blocks.toLocaleString()} recipe{gap.blocks === 1 ? '' : 's'}
          {gap.parent && ` · a kind of ${gap.parent}`}
        </span>
      </div>

      {gap.unlocks > 0 && (
        <p className="gap-row__unlocks">
          Match this and <strong>{gap.unlocks}</strong> recipe
          {gap.unlocks === 1 ? '' : 's'} become cookable — it is the only thing
          they are missing.
        </p>
      )}

      {gap.stocked_elsewhere.length > 0 ? (
        <p className="gap-row__elsewhere">
          Found at{' '}
          {gap.stocked_elsewhere.map((e, i) => (
            <span key={e.store}>
              {i > 0 && ', '}
              <strong>{e.store}</strong> (“{e.example}”)
            </span>
          ))}
          {' — '}so this shop's lookup missed it rather than not selling it.
        </p>
      ) : (
        <p className="gap-row__elsewhere muted">
          No store has matched this, so it may genuinely be hard to buy.
        </p>
      )}

      {gap.substitution && (
        <p className="gap-row__substitution">
          {gap.substitution.components.length > 0 && (
            <>Make it from {gap.substitution.components.join(', ')}</>
          )}
          {gap.substitution.components.length > 0 &&
            gap.substitution.substitutes.length > 0 &&
            ' · '}
          {gap.substitution.substitutes.length > 0 && (
            <>Or buy {gap.substitution.substitutes.join(', ')}</>
          )}
        </p>
      )}

      {gap.examples.length > 0 && (
        <p className="muted small">e.g. {gap.examples.join(' · ')}</p>
      )}

      <div className="gap-row__actions">
        <button
          type="button"
          className="btn btn--small"
          disabled={verdict.isPending}
          onClick={() => verdict.mutate({ ingredient: gap.id, carried: false })}
          title="Stop asking about this ingredient at this store"
        >
          They don't sell it here
        </button>
      </div>
    </li>
  );
}

export default function StoreCoverage() {
  const [storeId, setStoreId] = useState<number | null>(null);
  /* A blend with a description and a food nobody stocks are both gaps and are
     not the same job, so each half can be looked at on its own. */
  const [described, setDescribed] = useState(true);
  const [undescribed, setUndescribed] = useState(true);

  const substitution = described === undescribed ? null : described ? 'yes' : 'no';
  const noneWanted = !described && !undescribed;

  const { data: stores, isLoading } = useStoreCoverage(true);
  const { data: gaps, isLoading: gapsLoading } = useStoreGaps(
    noneWanted ? null : storeId,
    substitution,
  );

  return (
    <section className="review">
      <h1>What can each shop actually buy?</h1>
      {isLoading && <p className="center-note">Counting…</p>}

      <div className="coverage-grid">
        {(stores ?? []).map((store) => (
          <StoreCard
            key={store.id}
            store={store}
            selected={store.id === storeId}
            onSelect={() => setStoreId(store.id === storeId ? null : store.id)}
          />
        ))}
      </div>

      {storeId != null && (
        <>
          <h2>Worth fixing first at {gaps?.store.name ?? 'this shop'}</h2>
          <p className="muted">
            {gaps && (
              <>
                {' '}
                <strong>{gaps.unlockable.toLocaleString()}</strong> recipes here
                are exactly one match away.{' '}
                <strong>{gaps.gaps_with_substitution.toLocaleString()}</strong> of{' '}
                {gaps.gaps.toLocaleString()} gaps have a substitution recipe.
              </>
            )}
          </p>

          <div className="review-controls">
            <label>
              <input
                type="checkbox"
                checked={described}
                onChange={(event) => setDescribed(event.target.checked)}
              />
              Has a substitution recipe
            </label>
            <label>
              <input
                type="checkbox"
                checked={undescribed}
                onChange={(event) => setUndescribed(event.target.checked)}
              />
              No substitution recipe
            </label>
          </div>

          {noneWanted && <p className="muted">Tick one of the boxes to see the gaps.</p>}
          {gapsLoading && <p className="center-note">Working out what's missing…</p>}

          <ul className="gap-list">
            {(gaps?.results ?? []).map((gap) => (
              <GapRow key={gap.id} gap={gap} storeId={storeId} />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
